/**
 * Tab ids must match API keys where possible.
 * Sections: add new blocks under a tab without code changes beyond this config.
 */

/** Must match keys in GET /notify/notification_center/counters by_table. */
export const NOTIFICATION_CENTER_TAB_IDS = {
  REPORTING: "reporting",
  MESSAGES: "messages",
  CASES: "cases",
  CHARGE_INVOICES: "charge_invoices",
  FUND_REQUESTS: "fund_requests",
  PRODUCT: "product",
  SETTINGS: "settings",
  REGISTRATIONS: "registrations",
  DEADLINES: "deadlines",
};

/** @typedef {'text' | 'date' | 'datetime' | 'status' | 'registrationStatus' | 'fundRequestStatus' | 'money' | 'jurisdiction' | 'actions' | 'list' | 'casesSubject' | 'messagesDocuments' | 'period' | 'comment'} NotificationCenterColumnType */

/**
 * @typedef {Object} NotificationCenterColumnDef
 * @property {string} id
 * @property {string} field
 * @property {string} labelKey
 * @property {NotificationCenterColumnType} [type]
 */

/**
 * @typedef {Object} NotificationCenterSectionDef
 * @property {string} id
 * @property {string} titleKey
 * @property {string} [dataSectionKey] - nested key under tab in API (default: "default")
 * @property {NotificationCenterColumnDef[]} columns
 */

/**
 * @typedef {Object} NotificationCenterTabDef
 * @property {string} id
 * @property {string} labelKey
 * @property {string} panelID
 * @property {NotificationCenterSectionDef[]} sections
 */

const COL_COMPANY = {
  id: "company",
  field: "company",
  labelKey: "columns.company",
  type: "text",
};

const COL_STATUS = {
  id: "status",
  field: "status",
  labelKey: "columns.status",
  type: "status",
};

const COL_REGISTRATION_STATUS = {
  id: "status",
  field: "status",
  labelKey: "columns.status",
  type: "registrationStatus",
};

const COL_DATE_LAST_STATUS = {
  id: "date_last_status_changed",
  field: "date_last_status_changed",
  labelKey: "columns.dateLastStatusChanged",
  type: "datetime",
};

/** Registrations: date only (API often sends midnight → avoid "00:00"). */
const COL_DATE_LAST_STATUS_DATE = {
  ...COL_DATE_LAST_STATUS,
  type: "date",
};

const COL_GENERATED_BY = {
  id: "generated_by",
  field: "generated_by",
  labelKey: "columns.generatedBy",
  type: "text",
};

const COL_DUE_DATE = {
  id: "due_date",
  field: "due_date",
  labelKey: "columns.dueDate",
  type: "date",
};

const COL_ACTIONS = {
  id: "actions",
  field: "actions",
  labelKey: "columns.actions",
  type: "actions",
};

const COL_MESSAGES_DOCUMENTS = {
  id: "uploaded_documents",
  field: "uploaded_documents",
  labelKey: "columns.documents",
  type: "messagesDocuments",
};

/** Server: period + period_type + period_year (same display as VAT Returns). */
const COL_PERIOD = {
  id: "period",
  field: "period",
  labelKey: "columns.period",
  type: "period",
};

const STANDARD_STATUS_TRAIL = [
  COL_STATUS,
  COL_DATE_LAST_STATUS,
  COL_GENERATED_BY,
  COL_ACTIONS,
];

/** @returns {NotificationCenterTabDef[]} */
export function buildNotificationCenterTabs(t) {
  return [
    {
      id: NOTIFICATION_CENTER_TAB_IDS.REPORTING,
      labelKey: "tabs.reporting",
      panelID: "reporting",
      sections: [
        {
          id: "reporting_active",
          titleKey: "sections.reporting.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            COL_PERIOD,
            {
              id: "country_jurisdiction",
              field: "country_jurisdiction",
              labelKey: "columns.countryJurisdiction",
              type: "text",
            },
            {
              id: "type_of_report",
              field: "type_of_report",
              labelKey: "columns.typeOfReport",
              type: "text",
            },
            COL_DUE_DATE,
            ...STANDARD_STATUS_TRAIL,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.MESSAGES,
      labelKey: "tabs.messages",
      panelID: "messages",
      sections: [
        {
          id: "messages_not_closed",
          titleKey: "sections.messages.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            { id: "type", field: "type", labelKey: "columns.type", type: "text" },
            { id: "subject", field: "subject", labelKey: "columns.subject", type: "text" },
            COL_MESSAGES_DOCUMENTS,
            COL_STATUS,
            COL_DATE_LAST_STATUS,
            COL_GENERATED_BY,
            COL_ACTIONS,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.CASES,
      labelKey: "tabs.cases",
      panelID: "cases",
      sections: [
        {
          id: "cases_not_closed",
          titleKey: "sections.cases.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            {
              id: "subject",
              field: "subject",
              labelKey: "columns.subject",
              type: "casesSubject",
            },
            ...STANDARD_STATUS_TRAIL,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.CHARGE_INVOICES,
      labelKey: "tabs.chargeInvoices",
      panelID: "charge_invoices",
      sections: [
        {
          id: "charge_invoices_main",
          titleKey: "sections.chargeInvoices.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            { id: "app", field: "app", labelKey: "columns.app", type: "text" },
            { id: "amount", field: "amount", labelKey: "columns.amount", type: "text" },
            ...STANDARD_STATUS_TRAIL,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.FUND_REQUESTS,
      labelKey: "tabs.fundRequests",
      panelID: "fund_requests",
      sections: [
        {
          id: "fund_requests_main",
          titleKey: "sections.fundRequests.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            {
              id: "created",
              field: "created",
              fallbackFields: ["date", "created_at"],
              labelKey: "columns.created",
              type: "date",
            },
            {
              id: "description",
              field: "description",
              labelKey: "columns.description",
              type: "text",
            },
            {
              id: "jurisdiction",
              field: "jurisdiction",
              fallbackFields: ["country_jurisdiction"],
              labelKey: "columns.jurisdiction",
              type: "jurisdiction",
            },
            {
              id: "number",
              field: "number",
              fallbackFields: ["request_number"],
              labelKey: "columns.number",
              type: "text",
            },
            {
              id: "status",
              field: "status",
              labelKey: "columns.status",
              type: "fundRequestStatus",
            },
            {
              id: "requested",
              field: "requested",
              fallbackFields: ["request_amount", "requested_amount"],
              labelKey: "columns.requested",
              type: "money",
            },
            {
              id: "received",
              field: "received",
              fallbackFields: ["received_amount"],
              labelKey: "columns.received",
              type: "money",
            },
            {
              id: "balance",
              field: "balance",
              labelKey: "columns.balance",
              type: "money",
            },
            COL_ACTIONS,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.PRODUCT,
      labelKey: "tabs.product",
      panelID: "product",
      sections: [
        {
          id: "product_main",
          titleKey: "sections.product.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            { id: "app", field: "app", labelKey: "columns.app", type: "text" },
            {
              id: "description",
              field: "description",
              labelKey: "columns.description",
              type: "text",
            },
            ...STANDARD_STATUS_TRAIL,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.SETTINGS,
      labelKey: "tabs.settings",
      panelID: "settings",
      sections: [
        {
          id: "settings_main",
          titleKey: "sections.settings.main",
          dataSectionKey: "default",
          columns: [
            COL_COMPANY,
            {
              id: "type_of_number",
              field: "type_of_number",
              labelKey: "columns.typeOfNumber",
              type: "text",
            },
            {
              id: "jurisdiction",
              field: "jurisdiction",
              labelKey: "columns.jurisdiction",
              type: "text",
            },
            { id: "app", field: "app", labelKey: "columns.app", type: "text" },
            ...STANDARD_STATUS_TRAIL,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.REGISTRATIONS,
      labelKey: "tabs.registrations",
      panelID: "registrations",
      sections: [
        {
          id: "registrations_main",
          titleKey: "sections.registrations.main",
          dataSectionKey: "default",
          columns: [
            {
              id: "order_date",
              field: "order_date",
              labelKey: "columns.orderDate",
              type: "date",
            },
            COL_COMPANY,
            {
              id: "country_jurisdiction",
              field: "country_jurisdiction",
              labelKey: "columns.countryJurisdiction",
              type: "text",
            },
            {
              id: "type_of_registration",
              field: "type_of_registration",
              labelKey: "columns.typeOfRegistration",
              type: "text",
            },
            {
              id: "subcategory",
              field: "subcategory",
              labelKey: "columns.subcategoryOfRegistration",
              type: "list",
            },
            COL_REGISTRATION_STATUS,
            COL_DATE_LAST_STATUS_DATE,
            {
              id: "registrator",
              field: "registrator",
              labelKey: "columns.registrator",
              type: "text",
            },
            {
              id: "estimated_registration_date",
              field: "estimated_registration_date",
              labelKey: "columns.estimateDateOfRegistration",
              type: "date",
            },
            {
              id: "comment",
              field: "comment",
              fallbackFields: ["comments"],
              labelKey: "columns.comment",
              type: "comment",
            },
            COL_ACTIONS,
          ],
        },
      ],
    },
    {
      id: NOTIFICATION_CENTER_TAB_IDS.DEADLINES,
      labelKey: "tabs.deadlines",
      panelID: "deadlines",
      sections: [
        {
          id: "deadlines_main",
          titleKey: "sections.deadlines.main",
          dataSectionKey: "default",
          columns: [
            { id: "date", field: "date", labelKey: "columns.date", type: "date" },
            {
              id: "country_jurisdiction",
              field: "country_jurisdiction",
              labelKey: "columns.countryJurisdiction",
              type: "text",
            },
            {
              id: "type_of_report",
              field: "type_of_report",
              labelKey: "columns.typeOfReport",
              type: "text",
            },
            {
              id: "companies_list",
              field: "companies_list",
              labelKey: "columns.companiesList",
              type: "list",
            },
          ],
        },
      ],
    },
  ].map((tab) => ({
    ...tab,
    content: t(tab.labelKey),
  }));
}

/**
 * @param {Record<string, object>} dataByTab
 * @param {string} tabId
 * @param {NotificationCenterSectionDef} section
 */
export function getSectionRows(dataByTab, tabId, section) {
  const tabData = dataByTab?.[tabId];
  if (!tabData) return [];

  const sectionKey = section.dataSectionKey || "default";
  const sectionNode = tabData[sectionKey];

  if (Array.isArray(sectionNode)) {
    return sectionNode;
  }

  if (sectionNode?.items && Array.isArray(sectionNode.items)) {
    return sectionNode.items;
  }

  return [];
}

/**
 * @param {Record<string, object>} dataByTab
 * @param {string} tabId
 * @param {NotificationCenterSectionDef} section
 */
export function getSectionPagination(dataByTab, tabId, section) {
  const tabData = dataByTab?.[tabId];
  const sectionKey = section?.dataSectionKey || "default";
  const sectionNode = tabData?.[sectionKey];

  if (sectionNode && !Array.isArray(sectionNode)) {
    return {
      total: Number(sectionNode.total) || 0,
      offset: Number(sectionNode.offset) || 0,
      limit: Number(sectionNode.limit) || 20,
    };
  }

  const rows = getSectionRows(dataByTab, tabId, section);
  return {
    total: rows.length,
    offset: 0,
    limit: rows.length || 20,
  };
}
